import React from "react";
import Modal from "../../components/Modal/Modal";
import { durationToSeconds, formatTime } from "../../utils/timeFile";

// Shown for a note saved at a moment without any text.
export const EMPTY_NOTE_TEXT = "(no text)";

// The backend dedupes notes on clientId, so a retried save never doubles up.
export const newClientId = () =>
    `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const fmt = (seconds) => formatTime(Math.max(0, Math.round(seconds || 0)));

export const noteSeconds = (note) => (note?.timestampMs || 0) / 1000;

// First four words of the note, as the prototype titles it.
export const noteTitle = (text) => {
    const words = String(text || "").trim().split(/\s+/).slice(0, 4).join(" ");
    return words.length > 30 ? `${words.slice(0, 30)}…` : words || "Note";
};

export const recordingName = (audio) =>
    audio?.audio?.fileName?.replace(/\.[^.]+$/, "") || "Recording";

export const recordingSeconds = (audio) => durationToSeconds(audio?.audio?.timeDuration);

export const friendlyDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Keeps the fields the rest of the app reads (note, notesCount) in step with the notes list.
export const withNotes = (audio, notes) => {
    const sorted = [...notes].sort((a, b) => (a.timestampMs || 0) - (b.timestampMs || 0));
    const latest = sorted.reduce(
        (found, note) => (!found || new Date(note.updatedAt) > new Date(found.updatedAt) ? note : found),
        null
    );
    return { ...audio, notes: sorted, note: latest, notesCount: sorted.length };
};

export const toNote = (data) => ({
    _id: data?._id,
    clientId: data?.clientId ?? null,
    content: data?.content || "",
    timestampMs: data?.timestampMs || 0,
    createdAt: data?.createdAt,
    updatedAt: data?.updatedAt,
});

export const readErrorMessage = (result) =>
    result?.response?.data?.message ||
    result?.response?.data?.error ||
    result?.message ||
    "An error occurred";

const ICONS = {
    mic: <><rect x="9" y="2.5" width="6" height="11.5" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3.5" /></>,
    stop: <rect x="7" y="7" width="10" height="10" rx="2.5" />,
    pause: <path d="M9.5 5v14M14.5 5v14" />,
    play: <path d="M8 5.2l11 6.8-11 6.8z" />,
    back: <path d="M15 4.5L7.5 12l7.5 7.5" />,
    close: <path d="M6 6l12 12M18 6L6 18" />,
    chev: <path d="M9 5l7 7-7 7" />,
    search: <><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    note: <><path d="M5 3.5h14v12l-5 5H5z" /><path d="M14 20.5v-5h5M8.5 8.5h7M8.5 12.5h4" /></>,
    noteplus: <><path d="M5 3.5h14v12l-5 5H5z" /><path d="M14 20.5v-5h5" /><path d="M12 8.5v5.5M9.2 11.2h5.6" /></>,
    pin: <><path d="M12 21.5s-6.2-5.8-6.2-11.3a6.2 6.2 0 0 1 12.4 0c0 5.5-6.2 11.3-6.2 11.3z" /><circle cx="12" cy="10" r="2.2" /></>,
    edit: <path d="M4 20h4L19.5 8.5l-4-4L4 16z" />,
    trash: <path d="M4.5 6.5h15M9.5 6.5V4h5v2.5M6.5 6.5l1 13h9l1-13M10 10v6M14 10v6" />,
    star: <path d="M12 3.6l2.7 5.6 6 .8-4.4 4.2 1.1 6-5.4-3-5.4 3 1.1-6L3.3 10l6-.8z" />,
    back15: <path d="M11.5 7.5V3.5L6.5 7.5l5 4V7.5a6.5 6.5 0 1 1-6.3 8" />,
    fwd15: <path d="M12.5 7.5V3.5l5 4-5 4V7.5a6.5 6.5 0 1 0 6.3 8" />,
    import: <path d="M12 15.5V3.5M7.5 8L12 3.5 16.5 8M4.5 15v5h15v-5" />,
    folder: <path d="M3 6.5h6.2l2 2.2H21V19H3z" />,
};

export const Ic = ({ name, size = "" }) => (
    <svg className={`nt-i ${size}`} viewBox="0 0 24 24">{ICONS[name]}</svg>
);

// Left-aligned title with an optional back button, like the AI Tools header.
export const PageHeader = ({ title, onBack, actions, icon, tone }) => (
    <div className={`nt-header${tone ? ` vr-tone-${tone}` : ""}`}>
        <div className="nt-titlewrap">
            {onBack && <button type="button" className="nt-back" aria-label="Back" onClick={onBack}><Ic name="back" /></button>}
            {icon && <span className="vr-icon-tile nt-head-tile" aria-hidden="true">{icon}</span>}
            <h4><span>{title}</span></h4>
        </div>
        {actions && <div className="nt-actions">{actions}</div>}
    </div>
);

export const Spinner = () => (
    <div className="spinner-border nt-spinner" role="status">
        <span className="visually-hidden">Loading...</span>
    </div>
);

// Same delete confirmation the recording note uses.
export const ConfirmDialog = ({ title, message, confirmText = "Delete", busy, onConfirm, onCancel }) => (
    <Modal onClose={onCancel} modaltxt={title} closeButtonDisabled={busy}>
        <div className="nt nt-modal">
            <p className="nt-confirm-msg">{message}</p>
            <div className="nt-confirm-actions">
                <button type="button" className="nt-btn outline block" disabled={busy} onClick={onCancel}>Cancel</button>
                <button type="button" className="nt-btn red block" disabled={busy} onClick={onConfirm}>
                    {busy ? <Spinner /> : confirmText}
                </button>
            </div>
        </div>
    </Modal>
);
